import {
  type ChangeEvent,
  type FC,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from "react";
import { twMerge } from "tailwind-merge";

import { BUTTON_EVENT_CATEGORY, ComponentEventProperties, trackEvent } from "utils/amplitude";

export type RadioButtonProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> &
  ComponentEventProperties & {
    children?: ReactNode;
    indicatorClassName?: string;
    ref?: Ref<HTMLInputElement>;
  };

const INDICATOR_CLASSES = twMerge(
  "pointer-events-none flex size-4 items-center justify-center rounded-full border border-black-tint-70 bg-white",
  "peer-hover:border-black-tint-55",
  "peer-checked:border-blue-shade-20 peer-checked:bg-blue-shade-20 peer-checked:[&>span]:opacity-100",
  "peer-checked:peer-hover:border-blue peer-checked:peer-hover:bg-blue",
  "peer-disabled:cursor-not-allowed peer-disabled:border-black-tint-86",
  "peer-disabled:peer-hover:border-black-tint-86",
  "peer-checked:peer-disabled:border-black-tint-70 peer-checked:peer-disabled:bg-black-tint-70",
  "peer-checked:peer-disabled:peer-hover:border-black-tint-70 peer-checked:peer-disabled:peer-hover:bg-black-tint-70",
  "peer-focus-visible:ring-2 peer-focus-visible:ring-blue peer-focus-visible:ring-offset-1",
);

export const RadioButton: FC<RadioButtonProps> = ({
  children,
  className,
  indicatorClassName,
  ref,
  eventName,
  eventProperties,
  onChange,
  id,
  "aria-label": ariaLabel,
  ...inputProps
}) => {
  if (process.env.NODE_ENV !== "production" && !children && !ariaLabel) {
    console.error("RadioButton requires either children or an aria-label");
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (eventName) {
      trackEvent(eventName, {
        ...BUTTON_EVENT_CATEGORY,
        isEnabled: e.target.checked,
        ...eventProperties,
      });
    }
    onChange?.(e);
  };

  return (
    <label
      className={twMerge(
        "body-s flex items-center gap-2 text-black-tint-20 has-[:disabled]:cursor-not-allowed has-[:disabled]:text-black-tint-55",
        className,
      )}
    >
      <span className="relative size-4 shrink-0">
        <input
          {...inputProps}
          aria-label={ariaLabel}
          className="peer absolute inset-0 z-10 m-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          id={id}
          ref={ref}
          type="radio"
          onChange={handleChange}
        />
        <span className={twMerge(INDICATOR_CLASSES, indicatorClassName)} aria-hidden>
          <span className="size-1.5 rounded-full bg-white opacity-0" />
        </span>
      </span>
      {children}
    </label>
  );
};

RadioButton.displayName = "RadioButton";
